import { StyleSheet, View, ViewProps, useWindowDimensions } from 'react-native'
import {
  FloorplanPosition,
  WorkspaceData,
  Draft,
  FloorplanData,
  FloorplanWorkspaceData,
  useWorkspaceTypeElements,
  useWorkspaceTypes,
} from 'zens-store'
import {
  Editable,
  Color,
  Spacing,
  Text,
  Button,
  Icon,
  EditableItem,
  EditableItemInfo,
  Help,
  Table,
  TableRef,
  Radius,
} from 'zens-storybook'
import { Trans, useTranslation } from 'react-i18next'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { workspaceIsPlaced } from '@dms/helper'
import { useSortParam, useZoomParam } from 'zens-router'

import FloorplanAsset from './asset'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  menu: {
    borderColor: Color.calmGrey,
    borderRightWidth: 1,
    width: 360,
  },
  content: {
    flex: 1,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.S,
    minHeight: 73,
  },
  headContent: {
    flex: 1,
    flexDirection: 'row',
  },
  editor: {
    flex: 1,
    padding: Spacing.S,
    minHeight: 500,
  },
  title: {
    flex: 1,
    marginRight: Spacing.XS,
  },
  count: {
    color: Color.metalGrey,
    marginLeft: Spacing.XXS,
  },
  add: {
    alignSelf: 'flex-end',
  },
  workspaceType: {
    color: Color.metalGrey,
  },
  place: {
    backgroundColor: Color.greyBlue,
    borderColor: Color.greyBlue,
  },
  helpRight: {
    marginLeft: Spacing.S,
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
  },
  help: {
    flex: 1,
  },
  selected: {
    backgroundColor: Color.lightGreyBlue,
  },
  empty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    borderTopWidth: 1,
    borderColor: Color.calmGrey,
  },
  emtpyText: {
    marginHorizontal: Spacing.M,
    color: Color.metalGrey,
  },
  error: {
    fontSize: 14,
    marginTop: Spacing.XXS,
    color: Color.soothingPink,
  },
  skeletonEditor: {
    margin: Spacing.S,
    backgroundColor: Color.sleepingGrey,
    borderRadius: Radius.S,
  },
  skeletonAdd: {
    minWidth: 10,
  },
})

interface Props extends ViewProps {
  draft: Draft<FloorplanData>
  onSelect?: (workspaceId: number) => void
  onPlace?: (workspaceId: number) => void
  onDuplicate?: (workspaces: FloorplanWorkspaceData[]) => Promise<FloorplanWorkspaceData[] | void>
  onAdd?: () => void
  onRemove?: (workspaceId: number) => void
  renderHeader?: () => ReactElement
  errors?: Record<string, string>
}

const FloorplanOverview = (props: Props) => {
  const { draft, onSelect, onPlace, onAdd, onDuplicate, onRemove, renderHeader, errors, style, ...otherProps } = props
  const [selected, setSelected] = useState<string[]>([])
  const { height } = useWindowDimensions()
  const tableRef = useRef<TableRef>(null)
  const { workspaces = [], floorplan } = draft.data
  const { background, scale } = floorplan || {}
  const elements = useWorkspaceTypeElements()
  const worspaceTypes = useWorkspaceTypes()
  const { t } = useTranslation()
  const [sort, setSort] = useSortParam()
  const [zoom, setZoom] = useZoomParam()
  function lengthBetweenPositions(positions: FloorplanPosition[]) {
    return Math.sqrt((positions[0]?.x - positions[1]?.x) ** 2 + (positions[0]?.y - positions[1]?.y) ** 2)
  }
  const scaleRatio = scale ? lengthBetweenPositions([scale.start, scale.end]) / scale.distance : 1
  const tableStyle = {
    maxHeight: height - 302,
  }
  function isSelected(key: string) {
    return selected.indexOf(key) >= 0
  }

  function select(id: number) {
    onSelect?.(id)
  }
  const items: EditableItem[] = workspaces
    .map((item) => ({
      key: `${item.id}`,
      placement: item.position,
      selected: isSelected(`${item.id}`),
      filter: workspaceIsPlaced(item),
    }))
    .filter((item) => item.filter)

  const rows = workspaces.map((item) => ({
    data: { ...item, placed: workspaceIsPlaced(item) },
    style: isSelected(`${item.id}`) && styles.selected,
    onRemove: () => onRemove?.(item.id),
    ...(!workspaceIsPlaced(item) ? { accessory: false as const } : { onPress: () => select(item.id) }),
  }))

  const cols = [
    {
      key: 'name',
      title: t('Workspace'),
      render: renderWorkspace,
    },
    {
      key: 'placed',
      title: t('Action'),
      render: renderAction,
      size: {
        flex: -1,
      },
      style: {
        alignItems: 'flex-end',
      },
    },
  ]

  const lastSelectedRow = rows.find((row) => `${row.data.id}` === selected[selected.length - 1])
  useEffect(() => {
    if (lastSelectedRow) {
      tableRef.current?.scrollToRow({ row: lastSelectedRow, animated: true })
    }
  }, [lastSelectedRow])

  function update(value: EditableItemInfo[]) {
    const newWorkspaces = [...workspaces]
    const newSelected = [...selected]
    value.forEach((info) => {
      const { item } = info
      const workspaceIndex = newWorkspaces.findIndex((workspace) => `${workspace.id}` === item.key)
      newWorkspaces[workspaceIndex] = { ...newWorkspaces[workspaceIndex], position: item.placement }

      if (item.selected && !isSelected(item.key)) newSelected.push(item.key)
      if (!item.selected && isSelected(item.key)) newSelected.splice(newSelected.indexOf(item.key), 1)
    })
    draft.update('workspaces', newWorkspaces)
    setSelected(newSelected)
  }

  async function duplicate(value: EditableItemInfo[]) {
    const data = await onDuplicate?.(
      value.map((info) => {
        const { item } = info
        const workspace = draft.data.workspaces?.find((workspace) => `${workspace.id}` === item.key)
        const regExp = new RegExp(`^${workspace?.name?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} \\(\\d+\\)$`)
        const name = `${workspace?.name} (${draft.data.workspaces?.reduce(
          (acc, item) => acc + (regExp.test(`${item.name}`) ? 1 : 0),
          1,
        )})`
        return { ...workspace, id: NaN, position: item.placement, name }
      }),
    )
    if (data) {
      draft.update('workspaces', [...workspaces, ...data])
      setSelected(data.map((item) => `${item.id}`))
    }
  }

  function unplace(value: EditableItemInfo) {
    const newWorkspaces = [...workspaces]
    const newSelected = [...selected]
    const workspaceIndex = newWorkspaces.findIndex((workspace) => `${workspace.id}` === value.item.key)
    newWorkspaces[workspaceIndex] = {
      ...newWorkspaces[workspaceIndex],
      position: undefined,
    }
    newSelected.splice(newSelected.indexOf(value.item.key), 1)
    draft.update('workspaces', newWorkspaces)
    setSelected(newSelected)
  }

  function renderItem(info: EditableItemInfo) {
    const { item } = info
    const workspace = workspaces.find((workspace) => `${workspace.id}` === item.key)
    const elementId = workspace?.element_id
    const element = elements.data?.find((item) => item.id === elementId)
    return <FloorplanAsset uri={element?.data?.src} width={Math.round((element?.data?.width || 0) * scaleRatio)} />
  }
  function renderAction(item: FloorplanWorkspaceData) {
    if (workspaceIsPlaced(item)) return
    return (
      <Button
        left="markerAdd"
        title={t('Place')}
        round
        size="S"
        contentContainerStyle={styles.place}
        onPress={() => onPlace?.(item.id)}
        testID="FloorplanOverview.Place"
      />
    )
  }
  function renderWorkspace(item: WorkspaceData, key: string) {
    const { name, workspace_type_id } = item
    const workspaceType = worspaceTypes.data?.find((item) => item.id === workspace_type_id)
    const index = workspaces?.findIndex((workspace) => `${workspace.id}` === `${item.id}`)
    const error = errors?.[`workspaces.${index}.${key}`]
    return (
      <View style={[styles.content, isSelected(`${item.id}`) && styles.selected]}>
        <Text level={5}>{name}</Text>
        <Text level={7} style={styles.workspaceType}>
          {workspaceType?.data?.name}
        </Text>
        {error && (
          <Text style={styles.error} testID="FormField.Error">
            {error}
          </Text>
        )}
      </View>
    )
  }
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.menu}>
        <View style={styles.head}>
          {selected.length > 0 ? (
            <>
              <Text level={5} style={styles.title}>
                {t('{{count}} selected', { count: selected.length })}
              </Text>
              <View style={styles.helpRight}>
                <View style={styles.headContent}>
                  <Icon name="desk" color={Color.metalGrey} />
                  <Text style={styles.count}>{workspaces.length}</Text>
                </View>
                <Button left="plus" onPress={onAdd} size="S" style={styles.add} testID="FloorplanOverview.Add" />
              </View>
            </>
          ) : (
            <Help
              message={t(
                'All workspaces ever added to this floor are listed here. Changes in the workspace library affect pages and features that use workspaces.',
              )}
              style={styles.help}
              right={
                <View style={styles.helpRight}>
                  <View style={styles.headContent}>
                    <Icon name="desk" color={Color.metalGrey} />
                    <Text style={styles.count}>{workspaces.length}</Text>
                  </View>
                  <Button left="plus" onPress={onAdd} size="S" style={styles.add} testID="FloorplanOverview.Add" />
                </View>
              }
            >
              <Text level={5} style={styles.title}>
                <Trans>Workspace library</Trans>
              </Text>
            </Help>
          )}
        </View>

        {rows.length === 0 ? (
          <View style={styles.empty}>
            <Text style={styles.emtpyText}>
              <Trans>
                There are no workspaces added to this floor yet. Select the black plus-button above to add new
                workspaces. Adding workspaces will affect pages and features that use workspaces.
              </Trans>
            </Text>
          </View>
        ) : (
          <Table
            rows={rows}
            cols={cols}
            contentContainerStyle={tableStyle}
            sort={sort}
            onSortChange={setSort}
            ref={tableRef}
            getItemLayout={(_, index) => ({ length: 64, offset: 64 * index, index })}
          />
        )}
      </View>
      <View style={styles.content}>
        {renderHeader?.()}
        <Editable
          items={items}
          renderItem={renderItem}
          onChange={update}
          onDuplicate={duplicate}
          onRemove={unplace}
          style={styles.editor}
          background={<FloorplanAsset uri={background || ''} width={4000} />}
          snapTo={Math.round(10 * scaleRatio)}
          zoom={zoom}
          onZoom={setZoom}
        />
      </View>
    </View>
  )
}

interface SkeletonProps extends ViewProps {
  header?: ReactElement
}
const Skeleton = (props: SkeletonProps) => {
  const { header, style, ...otherProps } = props
  const { t } = useTranslation()
  const cols = [
    {
      key: 'name',
      title: t('Workspace'),
    },
    {
      key: 'placed',
      title: t('Action'),
      size: {
        flex: -1,
      },
      style: {
        alignItems: 'flex-end',
      },
    },
  ]
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.menu}>
        <View style={styles.head}>
          <Text.Skeleton style={styles.title} />
          {/* <View style={styles.helpRight}> */}
          <Button.Skeleton size="S" contentStyle={styles.skeletonAdd} />
          {/* </View> */}
        </View>
        <Table.Skeleton cols={cols} hasAccessory />
      </View>
      <View style={styles.content}>
        {header}
        <View style={[styles.editor, styles.skeletonEditor]} />
      </View>
    </View>
  )
}
FloorplanOverview.Skeleton = Skeleton
export default observer(FloorplanOverview)
